import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Maximize2, MapPin, Pencil, BarChart3, SlidersHorizontal, ChevronDown, Bell, Clock3, MessageSquare, Send, X, Eye, Play, Film, School, FileDown, Share2, FileEdit, CheckCircle2, RotateCcw, Volume2, VolumeX, Settings2 } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import MapOverviewTrafficLite from "src/newComponents/dashboard/MapOverviewTrafficLite";
import TrackingPageLite from "src/pages/tracking/TrackingPageLite";
import TrackingPageLite2 from "src/pages/tracking/TrackingPageLite2";
import TopRankingCard from "src/newComponents/dashboard/TopRankingCard";
import ReplayMapLite from "src/newComponents/dashboard/ReplayMapLite";
import TripReplayModal from "src/newComponents/dashboard/TripReplayModal";
import useTopRanking from "src/hooks/useTopRanking";
import useTripReplay, { transformTripData } from "src/hooks/useTripReplay";
import { RouteDeviationTrendChart } from "src/newComponents/dashboard/RouteDeviationTrendChart";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  ReferenceLine,
} from "recharts";

import {
  FiAlertTriangle,
  FiBell,
  FiChevronDown,
  FiGrid,
  FiMapPin,
  FiMenu,
  FiMinus,
  FiMoreHorizontal,
  FiPlus,
  FiSearch,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import { FaBus, FaMapMarkedAlt, FaSchool, FaUserCircle, FaUserGraduate } from "react-icons/fa";
import logo from "../../assets/images/logo.png";
import busYellow from "../../assets/images/bus1.png";
import busGreen from "../../assets/images/bus2.png";
import busGray from "../../assets/images/bus3.png";
import schoolImage from "../../assets/images/HILmr.png";

const alerts = [
  { title: "Exces de vitesse", bus: "Bus 23 - Ligne A1", time: "00:23", color: "bg-[#ff6f61]" },
  { title: "Arret non respecte", bus: "Bus 11 - Ligne B3", time: "07:10", color: "bg-[#f58c7a]" },
  { title: "Sortie d'itineraire", bus: "Bus 12 - Ligne C2", time: "07:30", color: "bg-[#f0b042]" },
  { title: "Retard signale", bus: "Bus 36 - Ligne D7", time: "07:30", color: "bg-[#33c4a1]" },
];

const busRows = [
  { image: busYellow, line: "L1", driver: "Jean Martin", speed: "42 km/h" },
  { image: busYellow, line: "S1", driver: "Aline Nguemo", speed: "28 km/h" },
  { image: busYellow, line: "75", driver: "Kouoh Marc", speed: "35 km/h" },
  { image: busYellow, line: "C3", driver: "Nadine Bella", speed: "30 km/h" },
];

const recentBusCards = [
  { image: busYellow, title: "Bus 12", badge: "40", line: "C1", status: "En circulation" },
  { image: busYellow, title: "Bus 25", badge: "16", line: "L1", status: "Ecole Grandson" },
  { image: busGreen, title: "Bus 8", badge: "14", line: "C3", status: "Ecole Elison" },
];

const studentPresence = [
  { image: busYellow, bus: "Bus 12", detail: "38 eleves pour l'ecole Grandson", speed: "42 km/h", status: "EN ROUTE" },
  { image: busYellow, bus: "Bus 6", detail: "31 eleves a bord", speed: "35 km/h", status: "A L'ARRET" },
  { image: busGray, bus: "Bus 7", detail: "29 eleves en descente", speed: "18 km/h", status: "RALENTI" },
];

const stopStats = [
  { label: "Arrets desservis", value: "56", color: "#62c2a7" },
  { label: "Eleves descendus", value: "188", color: "#45b7d9" },
  { label: "Montees validees", value: "188", color: "#6bbf7b" },
  { label: "Absences signalees", value: "12", color: "#f1685b" },
];

const maintenance = [
  { icon: "SP", value: "5", label: "Bus en maintenance", bg: "bg-[#d8f2e6]", text: "text-[#28a467]" },
  { icon: "EQ", value: "3", label: "Interventions prevues", bg: "bg-[#fef0d5]", text: "text-[#d39c2a]" },
  { icon: "CT", value: "2", label: "Controles a venir", bg: "bg-[#eef1f7]", text: "text-[#95a0bb]" },
];

const securityStats = [
  ["Incidents traites aujourd'hui", "bg-[#f7efe7]", "text-[#d18a43]"],
  ["Notifications envoyees", "bg-[#eef3fb]", "text-[#5c83c3]"],
  ["Presences confirmees", "bg-[#eef8f2]", "text-[#4dbf7f]"],
  ["Temps moyen de reaction", "bg-[#fff8dc]", "text-[#d6b12d]"],
  ["Zones surveillees actives", "bg-[#eef9fc]", "text-[#42b8d0]"],
];

const DASHBOARD_SECTION_OPTIONS = [
  { id: "topStats", label: "Statistiques principales" },
  { id: "mapAlerts", label: "Carte et alertes" },
  { id: "operations", label: "Operations quotidiennes" },
  { id: "fleet", label: "Flotte et maintenance" },
  { id: "kpis", label: "Bandeau KPI" },
  { id: "liveMaps", label: "Cartes live" },
  { id: "incidentDeviation", label: "Incidents et deviation" },
  { id: "speedZones", label: "Zones de vitesse" },
  { id: "trafficFlow", label: "Flux et vitesse moyenne" },
];

const DEFAULT_VISIBLE_SECTIONS = {
  topStats: false,
  mapAlerts: false,
  operations: false,
  fleet: false,
  kpis: true,
  liveMaps: true,
  incidentDeviation: true,
  speedZones: true,
  trafficFlow: true,
};

function Panel({ title, right, children, className = "" }) {
  return (
    <section className={`rounded-[22px] border border-[#e6ebf2] bg-white shadow-[0_10px_24px_rgba(14,27,44,0.04)] ${className}`}>
      <div className="flex flex-col gap-2 border-b border-[#eef2f7] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <h3 className="text-[1.1rem] font-bold text-[#253245]">{title}</h3>
        {right ? <div className="max-w-full overflow-x-auto text-sm text-[#94a0b2]">{right}</div> : null}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}
function MapPanel() {
  return (
    <Panel
      title="Activite des Trajets"
      right={
        <div className="flex items-center gap-3">
          <span>Vue temps reel</span>
          <div className="rounded-full bg-[#f4f7fb] px-3 py-1">Trajets du jour</div>
        </div>
      }
      className="overflow-hidden"
    >
      <div className="relative h-[300px] overflow-hidden rounded-[20px] border border-[#e2e6ec] bg-[#cbd3d6] sm:h-[360px]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 28%, rgba(95,208,214,0.28) 0 7%, transparent 7.4%), radial-gradient(circle at 70% 33%, rgba(95,208,214,0.22) 0 9%, transparent 9.4%), linear-gradient(180deg, rgba(255,255,255,0.18), rgba(27,57,83,0.1)), url(" +
              schoolImage +
              ")",
            backgroundSize: "auto, auto, auto, cover",
            backgroundPosition: "center",
            filter: "saturate(0.4) contrast(0.95)",
          }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))]" />

        <div className="absolute left-3 top-3 rounded-2xl bg-white/78 px-3 py-2 text-center shadow sm:left-5 sm:top-5 sm:px-4 sm:py-3">
          <div className="text-2xl font-bold text-[#283244] sm:text-4xl">28</div>
          <div className="text-xs text-[#97a4b5] sm:text-sm">Bus en circulation</div>
        </div>

        <div className="absolute right-3 top-3 flex flex-wrap items-center justify-end gap-2 sm:right-5 sm:top-5 sm:gap-3">
          <div className="rounded-xl bg-[#253245] px-3 py-2 text-xs font-semibold text-white sm:px-5 sm:py-3 sm:text-sm">
            <span className="mr-2 inline-block h-3 w-3 rounded-sm bg-[#2ac58f]" />
            LIVE
          </div>
          <div className="rounded-xl bg-[#253245] px-3 py-2 text-xs font-semibold text-white sm:px-5 sm:py-3 sm:text-sm">
            <FiMapPin className="mr-2 inline" />
            Zones
          </div>
          <button className="rounded-xl bg-white/90 p-2.5 text-[#253245] sm:p-3">
            <FiPlus />
          </button>
        </div>

        <div className="absolute right-3 top-20 flex flex-col gap-3 sm:right-5">
          <button className="rounded-xl bg-white/90 p-2.5 text-[#253245] sm:p-3">
            <FiGrid />
          </button>
        </div>

        <div className="absolute bottom-4 right-3 flex flex-col overflow-hidden rounded-xl border border-[#dde5ef] bg-white/92 text-[#253245] sm:bottom-6 sm:right-5">
          <button className="px-3 py-2 text-xl sm:px-4 sm:py-3">
            <FiPlus />
          </button>
          <button className="border-t border-[#e7edf5] px-3 py-2 text-xl sm:px-4 sm:py-3">
            <FiMinus />
          </button>
        </div>

        <div className="absolute bottom-4 left-3 right-16 hidden max-w-[300px] rounded-[22px] bg-white/96 shadow-[0_18px_40px_rgba(17,29,44,0.18)] md:block xl:bottom-auto xl:left-[35%] xl:right-auto xl:top-[23%] xl:w-[300px]">
          <div className="flex items-start justify-between border-b border-[#e9eef5] px-4 py-4">
            <div className="flex items-start gap-3">
              <img src={busYellow} alt="" className="mt-1 h-12 w-12 object-contain" />
              <div>
                <div className="text-[1.05rem] font-bold text-[#283244]">Bus 12 - Ecole Grandson</div>
                <div className="mt-1 text-sm text-[#8e9aab]">Ligne L1 - Grandson</div>
              </div>
            </div>
            <button className="text-xl text-[#93a0b1]">x</button>
          </div>
          <div className="space-y-3 px-4 py-4 text-sm text-[#6f7c8f]">
            <div className="flex items-center gap-2">
              <FiMapPin className="text-[#27ae60]" />
              Chauffeur : Jean Martin
            </div>
            <div className="flex items-center gap-2">
              <FaUserGraduate className="text-[#27ae60]" />
              Eleves a bord : 38
            </div>
            <div className="flex items-center justify-between border-t border-[#edf2f7] pt-3">
              <div>
                <div>Prochain arret : Alwa</div>
                <div className="mt-1">Statut : En circulation</div>
              </div>
              <span className="rounded-full bg-[#e8fbf3] px-4 py-2 text-sm font-bold text-[#1ea56f]">En circulation</span>
            </div>
          </div>
        </div>

        <div className="absolute left-[12%] top-[18%] rounded-full bg-[#43bfd0] px-4 py-2 text-sm font-bold text-white shadow">720</div>
        <div className="absolute left-[13%] top-[28%] rounded-full bg-[#f3aa2d] px-4 py-2 text-sm font-bold text-white shadow">20</div>
        <div className="absolute left-[66%] top-[20%] rounded-full bg-[#43bfd0] px-4 py-2 text-sm font-bold text-white shadow">1.315</div>
        <div className="absolute left-[76%] top-[20%] rounded-full bg-[#43bfd0] px-4 py-2 text-sm font-bold text-white shadow">1.290</div>
        <div className="absolute left-[70%] top-[40%] rounded-full bg-[#43bfd0] px-4 py-2 text-sm font-bold text-white shadow">S7 370</div>
        <div className="absolute left-[76%] top-[56%] rounded-full bg-[#f3aa2d] px-4 py-2 text-sm font-bold text-white shadow">6JT 260</div>

        <div className="absolute bottom-4 left-4 text-3xl font-semibold text-white/88">Google</div>
      </div>
    </Panel>
  );
}

function TrendSvg() {
  return (
    <svg viewBox="0 0 520 180" className="h-[180px] w-full">
      <path d="M50 145 L50 20 M50 145 L490 145" stroke="#d7e1ec" strokeWidth="2" fill="none" />
      <path d="M50 120 L140 96 L220 58 L305 38 L390 24 L465 18" stroke="#1bb7b6" strokeWidth="3" fill="none" />
      {[120, 96, 58, 38, 24, 18].map((y, i) => (
        <g key={i}>
          <circle cx={50 + i * 83} cy={y} r="5" fill="#1bb7b6" />
          <circle cx={50 + i * 83} cy={y} r="9" fill="rgba(27,183,182,0.12)" />
        </g>
      ))}
      {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"].map((d, i) => (
        <text key={d} x={50 + i * 83} y="170" textAnchor="middle" fontSize="16" fill="#748295">
          {d}
        </text>
      ))}
      {["4 000", "5 000", "6 000", "6 500"].map((v, i) => (
        <text key={v} x="10" y={145 - i * 35} fontSize="14" fill="#a0acbb">
          {v}
        </text>
      ))}
    </svg>
  );
}

const topStats = [
  { title: "Ecoles connectees", value: "88", icon: <FaSchool />, iconBg: "bg-[#0d8c7f]", delta: "+8%", deltaTone: "text-[#27ae60]" },
  { title: "Bus actifs", value: "325", icon: <img src={busYellow} alt="" className="h-11 w-11 object-contain" />, iconBg: "bg-[#f1f4f8]", delta: "+8%", deltaTone: "text-[#27ae60]" },
  { title: "Eleves transportes", value: "6,230", icon: <FiUsers />, iconBg: "bg-[#0d9b8a]", delta: "Actif", deltaTone: "text-[#27ae60]" },
  { title: "Lignes actives", value: "72", icon: <FaMapMarkedAlt />, iconBg: "bg-[#f1f4f8]", delta: "+5%", deltaTone: "text-[#27ae60]" },
  { title: "Alertes actives", value: "15", icon: <FiAlertTriangle />, iconBg: "bg-[#ff7c72]", delta: "+2", deltaTone: "text-[#f05b57]" },
  { title: "Trajets en cours", value: "241", icon: <img src={busGreen} alt="" className="h-11 w-11 object-contain" />, iconBg: "bg-[#f1f4f8]", delta: "+8%", deltaTone: "text-[#27ae60]" },
];

const mainKpiCards = [
  { label: "Volume de Trafic", value: "42", sub: "Véhicules captés", style: "from-emerald-100 to-emerald-200 border-emerald-200 text-emerald-900" },
  { label: "Vitesse Moyenne", value: "28 km/h", sub: "Dans zones écoles", style: "from-cyan-100 to-cyan-200 border-cyan-200 text-cyan-900" },
  { label: "Temps de Parcours", value: "24 min", sub: "Moyenne des trips", style: "from-blue-100 to-blue-200 border-blue-200 text-blue-900" },
  { label: "Nombre de Violations", value: "7", sub: "3 critiques", style: "from-teal-600 to-teal-700 border-teal-700 text-white" },
  { label: "Taux de Conformité", value: "XX", sub: "Véhicules conformes", style: "from-indigo-700 to-indigo-800 border-indigo-800 text-white" },
  { label: "Densité Temps Réel", value: "12", sub: "Cette minute", style: "from-violet-700 to-violet-800 border-violet-800 text-white" },
];

// Vitesse maximale par zone (pour affichage dans une card unique)
const maxSpeedByZone = [
  { zone: "20", speed: 67 },
  { zone: "30", speed: 52 },
  { zone: "50", speed: 45 },
  { zone: "80", speed: 38 },
  { zone: "90", speed: 29 },
];

const trendData = [
  { name: "Jan", green: 40, red: 60 },
  { name: "Fév", green: 35, red: 45 },
  { name: "Mar", green: 55, red: 50 },
  { name: "Avr", green: 20, red: 68 },
  { name: "Mai", green: 52, red: 38 },
  { name: "Juin", green: 45, red: 60 },
  { name: "Juil", green: 62, red: 56 },
  { name: "Août", green: 58, red: 52 },
];

const multiTrendData = [
  { name: "00h", volumeTrafic: 120, vitesseMoyenne: 65 },
  { name: "01h", volumeTrafic: 90, vitesseMoyenne: 70 },
  { name: "02h", volumeTrafic: 60, vitesseMoyenne: 75 },
  { name: "03h", volumeTrafic: 40, vitesseMoyenne: 78 },
  { name: "04h", volumeTrafic: 30, vitesseMoyenne: 80 },
  { name: "05h", volumeTrafic: 50, vitesseMoyenne: 72 },
  { name: "06h", volumeTrafic: 200, vitesseMoyenne: 55 },
  { name: "07h", volumeTrafic: 350, vitesseMoyenne: 45 },
  { name: "08h", volumeTrafic: 500, vitesseMoyenne: 40 },
  { name: "09h", volumeTrafic: 420, vitesseMoyenne: 48 },
  { name: "10h", volumeTrafic: 300, vitesseMoyenne: 55 },
  { name: "11h", volumeTrafic: 280, vitesseMoyenne: 60 },
  { name: "12h", volumeTrafic: 320, vitesseMoyenne: 58 },
  { name: "13h", volumeTrafic: 290, vitesseMoyenne: 62 },
  { name: "14h", volumeTrafic: 310, vitesseMoyenne: 59 },
  { name: "15h", volumeTrafic: 360, vitesseMoyenne: 52 },
  { name: "16h", volumeTrafic: 450, vitesseMoyenne: 46 },
  { name: "17h", volumeTrafic: 520, vitesseMoyenne: 38 },
  { name: "18h", volumeTrafic: 480, vitesseMoyenne: 42 },
  { name: "19h", volumeTrafic: 390, vitesseMoyenne: 50 },
  { name: "20h", volumeTrafic: 300, vitesseMoyenne: 58 },
  { name: "21h", volumeTrafic: 220, vitesseMoyenne: 65 },
  { name: "22h", volumeTrafic: 160, vitesseMoyenne: 70 },
  { name: "23h", volumeTrafic: 130, vitesseMoyenne: 72 },
];

const WEEKDAY_OPTIONS = [
  { value: "all", label: "Tous les jours" },
  { value: "monday", label: "Tous les lundis" },
  { value: "tuesday", label: "Tous les mardis" },
  { value: "wednesday", label: "Tous les mercredis" },
  { value: "thursday", label: "Tous les jeudis" },
  { value: "friday", label: "Tous les vendredis" },
  { value: "saturday", label: "Tous les samedis" },
  { value: "sunday", label: "Tous les dimanches" },
];

const DAY_FACTORS = {
  all: { volume: 1, speed: 1, incidents: 1, deviations: 1 },
  monday: { volume: 1.08, speed: 0.96, incidents: 1.07, deviations: 1.06 },
  tuesday: { volume: 1.04, speed: 0.98, incidents: 1.03, deviations: 1.02 },
  wednesday: { volume: 1.02, speed: 0.99, incidents: 1.01, deviations: 1.01 },
  thursday: { volume: 1.05, speed: 0.97, incidents: 1.04, deviations: 1.04 },
  friday: { volume: 1.15, speed: 0.93, incidents: 1.14, deviations: 1.15 },
  saturday: { volume: 0.86, speed: 1.07, incidents: 0.88, deviations: 0.9 },
  sunday: { volume: 0.74, speed: 1.11, incidents: 0.76, deviations: 0.78 },
};

const INCIDENT_SERIES = [
  { key: "hardAcceleration", label: "Accélération brusque", color: "#f97316" },
  { key: "overspeed", label: "Excès de vitesse", color: "#22c55e" },
  { key: "hardBraking", label: "Freinage brusque", color: "#eab308" },
  { key: "panicButton", label: "Panic bouton", color: "#ef4444" },
  { key: "speedLimitViolation", label: "Violation limite vitesse", color: "#3b82f6" },
];

const incidentTrendData = [
  { hour: "00h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "01h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "02h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "03h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "04h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "05h", panicButton: 3, hardAcceleration: 4, hardBraking: 3, overspeed: 5, speedLimitViolation: 4 },
  { hour: "06h", panicButton: 8, hardAcceleration: 8, hardBraking: 7, overspeed: 11, speedLimitViolation: 9 },
  { hour: "07h", panicButton: 12, hardAcceleration: 11, hardBraking: 10, overspeed: 12, speedLimitViolation: 11 },
  { hour: "08h", panicButton: 10, hardAcceleration: 10, hardBraking: 9, overspeed: 12, speedLimitViolation: 10 },
  { hour: "09h", panicButton: 5, hardAcceleration: 5, hardBraking: 4, overspeed: 6, speedLimitViolation: 5 },
  { hour: "10h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "11h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "12h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "13h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "14h", panicButton: 3, hardAcceleration: 4, hardBraking: 3, overspeed: 5, speedLimitViolation: 4 },
  { hour: "15h", panicButton: 8, hardAcceleration: 8, hardBraking: 7, overspeed: 11, speedLimitViolation: 9 },
  { hour: "16h", panicButton: 12, hardAcceleration: 11, hardBraking: 10, overspeed: 12, speedLimitViolation: 11 },
  { hour: "17h", panicButton: 10, hardAcceleration: 10, hardBraking: 9, overspeed: 12, speedLimitViolation: 10 },
  { hour: "18h", panicButton: 5, hardAcceleration: 5, hardBraking: 4, overspeed: 6, speedLimitViolation: 5 },
  { hour: "19h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "20h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "21h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "22h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
  { hour: "23h", panicButton: 2, hardAcceleration: 2, hardBraking: 1, overspeed: 3, speedLimitViolation: 2 },
];

const densityBars = [
  { name: "10", value: 3 },
  { name: "15", value: 5 },
  { name: "21", value: 4 },
  { name: "34", value: 8 },
  { name: "45", value: 6 },
  { name: "51", value: 9 },
  { name: "59", value: 7 },
  { name: "63", value: 5 },
  { name: "70", value: 8 },
];

const deviationSummaryCards = [
  { key: "reportedIncidents", title: "Incidents signalés", value: 21, unit: "" },
  { key: "delayDuration", title: "Durée des retards", value: 74, unit: "min" },
  { key: "userSatisfaction", title: "Satisfaction usagers", value: 68, unit: "%" },
  { key: "offRouteDrivers", title: "Taux de non respect des trajets prévus", value: 14, unit: "" },
];

function SmallTopCard({ item }) {
  return (
    <div className="flex min-h-[96px] items-center gap-3 rounded-[18px] border border-[#e7ebf1] bg-white px-4 py-3 shadow-[0_8px_20px_rgba(14,27,44,0.03)] sm:gap-4">
      <div className={`flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-[14px] text-[1.8rem] sm:h-[62px] sm:w-[62px] sm:text-[2rem] ${item.iconBg} ${item.iconBg === "bg-[#f1f4f8]" ? "text-[#536174]" : "text-white"}`}>
        {item.icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs text-[#7e8a9a] sm:text-sm">{item.title}</div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-[1.7rem] font-bold leading-none text-[#253245] sm:text-[2rem]">{item.value}</span>
          <span className={`rounded-full bg-[#eef9f3] px-2 py-0.5 text-xs font-bold ${item.deltaTone}`}>{item.delta}</span>
        </div>
      </div>
    </div>
  );
}

const speedLimitationZones = [
  {
    limitation: "50",
    title: "50 km/h",
    vehicles: 45,
    maxSpeed: 68,
    avgSpeed: 49,
    trendColor: "#22c55e",
    trendData: [
      { name: "00h", value: 5 },
      { name: "04h", value: 8 },
      { name: "08h", value: 15 },
      { name: "12h", value: 20 },
      { name: "16h", value: 18 },
      { name: "20h", value: 10 },
    ],
    colorTheme: "green",
    cardClass: "bg-green-50 border-green-200",
    titleClass: "text-green-800",
  },
  {
    limitation: "80",
    title: "80 km/h",
    vehicles: 80,
    maxSpeed: 102,
    avgSpeed: 76,
    trendColor: "#eab308",
    trendData: [
      { name: "00h", value: 6 },
      { name: "04h", value: 10 },
      { name: "08h", value: 16 },
      { name: "12h", value: 22 },
      { name: "16h", value: 19 },
      { name: "20h", value: 12 },
    ],
    colorTheme: "yellow",
    cardClass: "bg-yellow-50 border-yellow-200",
    titleClass: "text-yellow-800",
  },
  {
    limitation: "90",
    title: "90 km/h",
    vehicles: 65,
    maxSpeed: 121,
    avgSpeed: 84,
    trendColor: "#ef4444",
    trendData: [
      { name: "00h", value: 5 },
      { name: "04h", value: 8 },
      { name: "08h", value: 15 },
      { name: "12h", value: 20 },
      { name: "16h", value: 18 },
      { name: "20h", value: 10 },
    ],
    colorTheme: "red",
    cardClass: "bg-red-50 border-red-200",
    titleClass: "text-red-800",
  },
  {
    limitation: "110",
    title: "110 km/h",
    vehicles: 120,
    maxSpeed: 137,
    avgSpeed: 103,
    trendColor: "#8b5cf6",
    trendData: [
      { name: "00h", value: 7 },
      { name: "04h", value: 11 },
      { name: "08h", value: 17 },
      { name: "12h", value: 23 },
      { name: "16h", value: 20 },
      { name: "20h", value: 13 },
    ],
    colorTheme: "purple",
    cardClass: "bg-purple-50 border-purple-200",
    titleClass: "text-purple-800",
  },
];

const conformiteItems = [
  { label: "Véhicules ne respectant pas les limitations de vitesse", value: 34, color: "bg-red-500" },
  { label: "Véhicules roulants non-conformes", value: 19, color: "bg-orange-500" },
  { label: "Nombre de non-respects des feux de signalisation", value: 27, color: "bg-amber-500" },
];

const ecologieItems = [
  { label: "Usagers en transports en commun ou mode écologique", value: "42%" },
  { label: "Taux d'utilisateurs de vélo ou de covoiturage", value: "18%" },
];

const feedbackDataLeft = [
  { name: "A", value: 45, color: "#10E3BB" },
  { name: "B", value: 24, color: "#FFD21C" },
  { name: "C", value: 18, color: "#E849A0" },
  { name: "D", value: 13, color: "#19B2FF" },
];

const feedbackDataRight = [
  { name: "A", value: 52, color: "#10E3BB" },
  { name: "B", value: 19, color: "#FFD21C" },
  { name: "C", value: 16, color: "#E849A0" },
  { name: "D", value: 13, color: "#7D73FF" },
];

const alertItems = [
  { id: 1, color: "#ef4444", label: "Niveau critique", arrivedAt: "08:12", status: "Urgent", vehicle: "Véhicule CE-002" },
  { id: 2, color: "#f97316", label: "Congestion du trafic", arrivedAt: "08:27", status: "Moyen", vehicle: "Véhicule LT-004" },
  { id: 3, color: "#eab308", label: "Péage autoroutier", arrivedAt: "08:41", status: "Normal", vehicle: "Véhicule OU-003" },
  { id: 4, color: "#fb7185", label: "Priorité élevée", arrivedAt: "09:33", status: "Urgent", vehicle: "Véhicule CE-001" },
  { id: 5, color: "#22c55e", label: "Signalement social", arrivedAt: "10:05", status: "Normal", vehicle: "Véhicule LT-001" },
  { id: 6, color: "#ef4444", label: "Alerte collision", arrivedAt: "10:14", status: "Urgent", vehicle: "Véhicule CE-004" },
  { id: 7, color: "#f97316", label: "Bouchon axe Nord", arrivedAt: "10:22", status: "Moyen", vehicle: "Véhicule LT-006" },
  { id: 8, color: "#eab308", label: "Ralentissement péage", arrivedAt: "10:37", status: "Normal", vehicle: "Véhicule OU-006" },
  { id: 9, color: "#fb7185", label: "Arrêt non autorisé", arrivedAt: "10:45", status: "Moyen", vehicle: "Véhicule CE-007" },
  { id: 10, color: "#22c55e", label: "Signalement usager", arrivedAt: "10:58", status: "Info", vehicle: "Véhicule LT-008" },
  { id: 11, color: "#ef4444", label: "Incident majeur", arrivedAt: "11:06", status: "Urgent", vehicle: "Véhicule CE-010" },
  { id: 12, color: "#f97316", label: "Déviation saturée", arrivedAt: "11:20", status: "Moyen", vehicle: "Véhicule OU-009" },
  { id: 13, color: "#eab308", label: "Alerte signalisation", arrivedAt: "11:33", status: "Normal", vehicle: "Véhicule LT-011" },
  { id: 14, color: "#fb7185", label: "Risque de blocage", arrivedAt: "11:47", status: "Urgent", vehicle: "Véhicule CE-012" },
];

const commentItems = [
  { id: 101, color: "#10b981", label: "Le trafic est fluide ce matin.", arrivedAt: "08:49", author: "Usager A" },
  { id: 102, color: "#38bdf8", label: "Ralentissement au rond-point central.", arrivedAt: "09:02", author: "Usager B" },
  { id: 103, color: "#f59e0b", label: "Signalisation peu visible à l'entrée Est.", arrivedAt: "09:15", author: "Usager C" },
  { id: 104, color: "#a78bfa", label: "Déviation bien indiquée mais trop longue.", arrivedAt: "09:48", author: "Usager D" },
];

const incidentRows = [
  {
    vehicle: "Véhicule CE-002",
    incidentDate: "2026-02-19 08:12",
    entryDate: "2026-02-19 08:12",
    exitDate: "2026-02-19 08:32",
    location: "Carrefour Logpom",
    type: "Panic bouton",
  },
  {
    vehicle: "Véhicule LT-004",
    incidentDate: "2026-02-19 08:48",
    entryDate: "2026-02-19 08:48",
    exitDate: "2026-02-19 09:10",
    location: "Boulevard Deido",
    type: "Accélération brusque",
  },
  {
    vehicle: "Véhicule OU-003",
    incidentDate: "2026-02-19 09:22",
    entryDate: "2026-02-19 09:22",
    exitDate: "2026-02-19 09:44",
    location: "Rond-point Bonamoussadi",
    type: "Freinage brusque",
  },
  {
    vehicle: "Véhicule CE-001",
    incidentDate: "2026-02-19 09:55",
    entryDate: "2026-02-19 09:55",
    exitDate: "2026-02-19 10:21",
    location: "Axe Douala V",
    type: "Excès de vitesse",
  },
  {
    vehicle: "Véhicule LT-001",
    incidentDate: "2026-02-19 10:35",
    entryDate: "2026-02-19 10:35",
    exitDate: "2026-02-19 11:02",
    location: "Entrée Zone École",
    type: "Violation limite vitesse",
  },
];

// Mock trip data for replay modal
const generateTripData = (incident) => {
  const duration = 24; // minutes
  const speedData = [];
  const positions = [];
  
  // Coordonnées de départ et d'arrivée (Douala, Cameroun)
  const startLat = 4.0500;
  const startLng = 9.7000;
  const endLat = 4.0700;
  const endLng = 9.7200;
  
  for (let i = 0; i <= duration * 2; i += 0.5) {
    const mins = 30 + Math.floor(i / 60);
    const secs = Math.floor((i % 60) * 60);
    const time = `07:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    const baseSpeed = 25 + Math.sin(i / 4) * 10;
    const isViolation = i >= 24 && i <= 28; // Violation period
    const speed = isViolation ? 45 + Math.random() * 10 : baseSpeed;
    
    // Interpolation linéaire entre le départ et l'arrivée avec léger bruit
    const progress = i / (duration * 2);
    const lat = startLat + (endLat - startLat) * progress + (Math.random() - 0.5) * 0.002;
    const lng = startLng + (endLng - startLng) * progress + (Math.random() - 0.5) * 0.002;
    
    speedData.push({
      time,
      speed: Math.round(speed),
      isViolation,
      limit: 30,
    });
    
    positions.push({
      time: i,
      lat,
      lng,
      speed: Math.round(speed),
      isViolation,
    });
  }
  
  return {
    tripId: `TRIP-${incident?.incidentDate?.replace(/[-: ]/g, '').slice(0, 8)}-0042`,
    vehicleId: incident?.vehicle || "BUS-07",
    schoolName: "École Joffre",
    destinationName: "Lycée Victor Hugo",
    mode: incident?.type?.includes("Panic") ? "Dépôt" : "Ramassage",
    date: incident?.incidentDate?.split(' ')[0] || "27/02/2026",
    duration,
    distance: 8.2,
    maxSpeed: 52,
    violationsCount: 1,
    speedData,
    positions,
    events: [
      { time: "07:30:00", type: "start", label: "Départ - École Joffre", icon: "🟢" },
      { time: "07:32:15", type: "info", label: "Vitesse: 28 km/h", icon: "ℹ️" },
      { time: "07:35:42", type: "info", label: "Zone traversée: Rue de la Paix", icon: "ℹ️" },
      { time: "07:42:15", type: "violation", label: "VIOLATION - 52 km/h (lim 30)", icon: "🔴" },
      { time: "07:45:30", type: "info", label: "Retour vitesse normale: 25 km/h", icon: "ℹ️" },
      { time: "07:54:00", type: "end", label: "Arrivée - Lycée Victor Hugo", icon: "🟓" },
    ],
  };
};

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const INCIDENTS_PAGE_SIZE = 4;

const tileSelect = (
  <span className="inline-flex items-center gap-1 rounded-full bg-secondary border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
    <ChevronDown className="w-3 h-3" />
  </span>
);

const DarkCard = ({ title, action, children, className = "" }) => (
  <div className={`rounded-2xl bg-card border border-border shadow-sm ${className}`}>
    <div className="px-4 pt-3 pb-1 flex items-center justify-between">
      <h3 className="text-base md:text-lg font-semibold text-foreground tracking-tight">{title}</h3>
      {action}
    </div>
    <div className="p-4 pt-2">{children}</div>
  </div>
);

const EMPTY_FILTERS = {};

const DeviationTopRankingCard = () => {
  const options = [
    {
      value: "driversOffRoute",
      label: "Chauffeur aux Trajets Déviés",
      category: "drivers",
      metric: "deviations",
      valueLabel: "Déviations",
    },
  ];
  const [selectedType, setSelectedType] = useState(options[0].value);
  const selectedOption = options.find((opt) => opt.value === selectedType) || options[0];

  const { data, loading, error } = useTopRanking({
    category: selectedOption.category,
    metric: selectedOption.metric,
    topX: 20,
    period: "week",
    filters: selectedOption.filters || EMPTY_FILTERS,
  });

  return (
    <div className="rounded-xl border border-border bg-card p-2">
      {loading ? (
        <div className="text-xs text-muted-foreground py-2">Chargement du classement...</div>
      ) : error ? (
        <div className="text-xs text-red-500 py-2">Erreur de chargement du classement</div>
      ) : (
        <TopRankingCard
          title=""
          data={data}
          topX={5}
          fixedPageSize={5}
          showTopXSelector={false}
          colorTheme="orange"
          compact={true}
          showPagination={true}
          emptyMessage="Aucune donnée disponible"
          headerControls={
            <div className="flex flex-wrap items-center gap-1 min-w-0">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-40 max-w-full text-xs border border-gray-300 rounded px-2 py-1 pr-6 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 truncate min-w-0"
              >
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200 text-[10px] font-semibold shadow-sm max-w-full truncate">
                {selectedOption.label}
              </span>
            </div>
          }
        />
      )}
    </div>
  );
};

const WeekdayFilterSelect = ({ value, onChange }) => (
  <div className="mb-2">
    <select
      value={value}
      onChange={onChange}
      className="h-10 min-w-[180px] rounded-lg border border-border bg-card px-3 pr-8 text-sm leading-5 text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      {WEEKDAY_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const LimitationZoneRankingCard = ({ limitation, colorTheme, label }) => {
  const { data, loading, error } = useTopRanking({
    category: "vehicles",
    metric: "violations",
    topX: 10,
    period: "week",
    filters: { limitation },
  });

  if (loading) {
    return <div className="text-xs text-muted-foreground py-2">Chargement du classement...</div>;
  }
  if (error) {
    return <div className="text-xs text-red-500 py-2">Erreur de chargement du classement</div>;
  }

  return (
    <TopRankingCard
      title=""
      data={data}
      topX={5}
      fixedPageSize={5}
      showTopXSelector={false}
      compact={true}
      showPagination={true}
      colorTheme={colorTheme}
      emptyMessage="Aucune donnée disponible"
      headerControls={
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/90 border text-[10px] font-semibold text-gray-700">
          {label}
        </span>
      }
    />
  );
};

// Mapping des véhicules - à remplacer par des données réelles ou une API
const VEHICLE_MAPPING = {
  "Véhicule CE-002": { uniqueId: "it_864714067581190", vehicleNumber: "BUS 1" },
  "Véhicule LT-004": { uniqueId: "it_863406070712026", vehicleNumber: "BUS 2" },
  "Véhicule OU-003": { uniqueId: "it_863406070676817", vehicleNumber: "BUS 3" },
  "Véhicule CE-001": { uniqueId: "it_864714067575200", vehicleNumber: "BUS 4" },
  "Véhicule LT-001": { uniqueId: "it_863406070710988", vehicleNumber: "BUS 5" },
};

const TrafficPageLite = forwardRef((_, ref) => {
  const mapOverviewRef = useRef(null);
  const outletContext = useOutletContext() || {};
  const { allAreas = [], site = null, zone = null } = outletContext;
  const [commentInput, setCommentInput] = useState("");
  const [incidentPage, setIncidentPage] = useState(1);
  const [selectedIncidentSeriesKey, setSelectedIncidentSeriesKey] = useState(null);
  const [weekdayFilter, setWeekdayFilter] = useState("all");
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [alertResponses, setAlertResponses] = useState({});
  const [sentResponses, setSentResponses] = useState({});
  const [alertsModalPage, setAlertsModalPage] = useState(1);
  const [isTrackingExpanded, setIsTrackingExpanded] = useState(false);
  const [isReplayModalOpen, setIsReplayModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [isLayoutSettingsOpen, setIsLayoutSettingsOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState(() => ({ ...DEFAULT_VISIBLE_SECTIONS }));

  const visibleSectionCount = useMemo(
    () => Object.values(visibleSections).filter(Boolean).length,
    [visibleSections]
  );

  const filteredMultiTrendData = useMemo(() => {
    const factor = DAY_FACTORS[weekdayFilter] || DAY_FACTORS.all;
    return multiTrendData.map((item) => ({
      ...item,
      volumeTrafic: Math.round(item.volumeTrafic * factor.volume),
      vitesseMoyenne: Math.round(item.vitesseMoyenne * factor.speed),
    }));
  }, [weekdayFilter]);

  const filteredIncidentTrendData = useMemo(() => {
    const factor = DAY_FACTORS[weekdayFilter] || DAY_FACTORS.all;
    return incidentTrendData.map((item) => ({
      ...item,
      panicButton: Math.min(12, Math.max(0, Math.round(item.panicButton * factor.incidents))),
      hardAcceleration: Math.min(12, Math.max(0, Math.round(item.hardAcceleration * factor.incidents))),
      hardBraking: Math.min(12, Math.max(0, Math.round(item.hardBraking * factor.incidents))),
      overspeed: Math.min(12, Math.max(0, Math.round(item.overspeed * factor.incidents))),
      speedLimitViolation: Math.min(12, Math.max(0, Math.round(item.speedLimitViolation * factor.incidents))),
    }));
  }, [weekdayFilter]);

  const activeIncidentSeries = useMemo(
    () => INCIDENT_SERIES.filter((series) => !selectedIncidentSeriesKey || series.key === selectedIncidentSeriesKey),
    [selectedIncidentSeriesKey]
  );

  const selectedIncidentLabel = useMemo(
    () => INCIDENT_SERIES.find((series) => series.key === selectedIncidentSeriesKey)?.label || null,
    [selectedIncidentSeriesKey]
  );

  const filteredIncidentRows = useMemo(() => {
    if (!selectedIncidentLabel) return incidentRows;
    return incidentRows.filter((row) => row.type === selectedIncidentLabel);
  }, [selectedIncidentLabel]);

  // Calcul des dates pour l'API à partir de l'incident sélectionné
  const tripQueryParams = useMemo(() => {
    if (!selectedIncident) return null;
    
    const vehicleInfo = VEHICLE_MAPPING[selectedIncident.vehicle];
    if (!vehicleInfo) return null;

    // Parser la date de l'incident
    const incidentDate = new Date(selectedIncident.incidentDate.replace(' ', 'T'));
    const entryDate = new Date(selectedIncident.entryDate.replace(' ', 'T'));
    const exitDate = new Date(selectedIncident.exitDate.replace(' ', 'T'));

    // Convertir en timestamp Unix
    const fromTimestamp = Math.floor(entryDate.getTime() / 1000).toString();
    const toTimestamp = Math.floor(exitDate.getTime() / 1000).toString();

    return {
      uniqueId: vehicleInfo.uniqueId,
      from: fromTimestamp,
      to: toTimestamp,
      vehicleNumber: vehicleInfo.vehicleNumber,
      incidentDate: selectedIncident.incidentDate,
    };
  }, [selectedIncident]);

  // Hook pour récupérer les données de trajet réelles (avec fallback mock si vide)
  const { tripData: apiTripData, loading: tripLoading, error: tripError } = useTripReplay(
    tripQueryParams ? { ...tripQueryParams, useMockData: true } : { uniqueId: null, from: null, to: null, useMockData: true }
  );

  // Transformer les données API ou mock
  const tripData = useMemo(() => {
    if (!selectedIncident) return null;

    // Si on a des données (API ou mock), les transformer
    if (apiTripData?.points && apiTripData.points.length > 0) {
      const transformed = transformTripData(apiTripData.points, 30);
      const vehicleInfo = VEHICLE_MAPPING[selectedIncident.vehicle];

      return {
        ...transformed,
        tripId: `TRIP-${selectedIncident.incidentDate?.replace(/[-: ]/g, '').slice(0, 8)}-${vehicleInfo?.vehicleNumber || '00'}`,
        vehicleId: vehicleInfo?.vehicleNumber || selectedIncident.vehicle,
        schoolName: selectedIncident.location,
        destinationName: selectedIncident.location,
        mode: selectedIncident.type?.includes("Panic") ? "Dépôt" : "Ramassage",
        date: selectedIncident.incidentDate?.split(' ')[0] || "27/02/2026",
      };
    }

    // Fallback ultime: utiliser les données mockées générées
    return generateTripData(selectedIncident);
  }, [selectedIncident, apiTripData]);

  const totalIncidentPages = Math.max(1, Math.ceil(filteredIncidentRows.length / INCIDENTS_PAGE_SIZE));
  const paginatedIncidentRows = filteredIncidentRows.slice(
    (incidentPage - 1) * INCIDENTS_PAGE_SIZE,
    incidentPage * INCIDENTS_PAGE_SIZE
  );

  useEffect(() => {
    if (incidentPage > totalIncidentPages) {
      setIncidentPage(totalIncidentPages);
    }
  }, [incidentPage, totalIncidentPages]);

  const alertCommentTypes = useMemo(
    () => [
      { key: "alertes", label: "Alertes", count: alertItems.length },
      { key: "commentaires", label: "Commentaires", count: commentItems.length },
    ],
    []
  );

  const alertModalPageSize = 10;
  const modalItems = selectedItemType === "commentaires" ? commentItems : alertItems;
  const totalAlertModalPages = Math.max(1, Math.ceil(alertItems.length / alertModalPageSize));
  const paginatedAlertItems = alertItems.slice(
    (alertsModalPage - 1) * alertModalPageSize,
    alertsModalPage * alertModalPageSize
  );
  const displayedModalItems = selectedItemType === "alertes" ? paginatedAlertItems : modalItems;

  useImperativeHandle(ref, () => ({
    getMapOverview: () => mapOverviewRef.current,
    getDrawShape: () => mapOverviewRef.current?.handleSetDrawShape,
    getDrawAllAois: () => mapOverviewRef.current?.handleSetDrawAllAois,
  }));

  const toggleSectionVisibility = (sectionId) => {
    setVisibleSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const setAllSectionsVisibility = (nextValue) => {
    setVisibleSections(
      DASHBOARD_SECTION_OPTIONS.reduce((acc, section) => {
        acc[section.id] = nextValue;
        return acc;
      }, {})
    );
  };

  return (
    <div className="min-h-screen bg-background p-2 md:p-4">
      <div className="relative w-full rounded-[22px] border border-border bg-card p-3 md:p-5 shadow-sm">
        <div className="absolute left-0 top-0 bottom-0 w-6 rounded-l-[22px]" />

        <div className="ml-5">
          <div className="mb-4 flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-4 py-3">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">
                  Dashboard Transport Scolaire
                </h1>
                {/* <p className="text-sm text-muted-foreground">
                  Suivi en temps réel des véhicules et violations
                </p> */}
              </div>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsLayoutSettingsOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-secondary"
              >
                <Settings2 className="h-4 w-4" />
                Blocs
              </button>

              {isLayoutSettingsOpen && (
                <div className="absolute right-0 top-[calc(100%+0.75rem)] z-30 w-[320px] rounded-2xl border border-border bg-card p-4 shadow-2xl">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">Affichage du dashboard</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {visibleSectionCount} bloc{visibleSectionCount > 1 ? "s" : ""} visible{visibleSectionCount > 1 ? "s" : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsLayoutSettingsOpen(false)}
                      className="rounded-full border border-border p-1.5 text-muted-foreground transition-colors hover:bg-secondary"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-4 grid gap-2">
                    {DASHBOARD_SECTION_OPTIONS.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => toggleSectionVisibility(section.id)}
                        className={`flex items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition-colors ${
                          visibleSections[section.id]
                            ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                            : "border-border bg-secondary/30 text-muted-foreground"
                        }`}
                      >
                        <span>{section.label}</span>
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${
                            visibleSections[section.id] ? "bg-emerald-500" : "bg-slate-300"
                          }`}
                        />
                      </button>
                    ))}
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAllSectionsVisibility(true)}
                      className="flex-1 rounded-xl bg-foreground px-3 py-2 text-sm font-medium text-background transition-opacity hover:opacity-90"
                    >
                      Tout afficher
                    </button>
                    <button
                      type="button"
                      onClick={() => setAllSectionsVisibility(false)}
                      className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                    >
                      Tout masquer
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={`space-y-4 " ${
    visibleSections.liveMaps && visibleSections.topStats ? "mb-4 " : ""
  }`}>
            {visibleSections.topStats && (
              <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-6  ">
                {topStats.map((item) => (
                  <SmallTopCard key={`${item.title}-${item.value}`} item={item} />
                ))}
              </div>
            )}

            {visibleSections.mapAlerts && (
              <div className="grid gap-4 xl:grid-cols-[1fr_300px] ">
                <MapPanel />

                <Panel title="Alertes recentes" right={<span className="rounded-full bg-[#eef7f6] px-3 py-1 text-[#28a88e]">Alerte</span>}>
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div key={`${alert.title}-${alert.time}`} className="flex items-start gap-3 rounded-[18px] bg-[#f8fafc] p-3">
                        <div className={`mt-1 flex h-10 w-10 items-center justify-center rounded-full text-white ${alert.color}`}>
                          <FiBell />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-semibold text-[#283244]">{alert.title}</div>
                              <div className="mt-1 text-sm text-[#8d98a7]">{alert.bus}</div>
                            </div>
                            <span className="text-sm text-[#7e8b9e]">{alert.time}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>
            )}

            {visibleSections.operations && (
            <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-[340px_minmax(0,1fr)_340px_300px]">
              <div className="space-y-4">
                <Panel title="Activite des trajets" right={<span className="rounded-full bg-[#eef3fb] px-3 py-1">Aujourd'hui</span>}>
                  <div className="grid grid-cols-1 gap-4 border-b border-[#eef2f7] pb-4 sm:grid-cols-3">
                    {[
                      ["187", "Termines", "#1bb7b6"],
                      ["54", "En cours", "#56657b"],
                      ["8", "Annules", "#f05b57"],
                    ].map(([value, label, color]) => (
                      <div key={label}>
                        <div className="text-[2.2rem] font-bold" style={{ color }}>{value}</div>
                        <div className="text-sm text-[#7e8a9a]">{label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5">
                    <div className="h-5 overflow-hidden rounded-full bg-[#edf2f7]">
                      <div className="flex h-full">
                        <div className="w-[52%] bg-[#65ddb0]" />
                        <div className="w-[12%] bg-[#18a0e8]" />
                        <div className="w-[10%] bg-[#f58996]" />
                        <div className="flex-1 bg-[#dae4f2]" />
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-[#7e8a9a]">
                      <span>137 trajets effectues sur 141 planifies</span>
                      <span className="rounded-xl bg-[#f7dfd5] px-3 py-1 font-bold text-[#c26a57]">4 retards</span>
                    </div>
                  </div>
                </Panel>

                <Panel title="Zones de surveillance" right={<span className="rounded-full bg-[#eef3fb] px-3 py-1">124 zones</span>}>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {[
                      ["124", "Zones actives", "#253245"],
                      ["42", "Entrees detectees", "#1bb7d3"],
                      ["6", "Violations", "#56657b"],
                    ].map(([value, label, color]) => (
                      <div key={label}>
                        <div className="text-[2.2rem] font-bold" style={{ color }}>{value}</div>
                        <div className="text-sm text-[#7e8a9a]">{label}</div>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>

              <div className="space-y-4">
                <Panel title="Evolution des eleves transportes" right={<span className="rounded-full bg-[#eef3fb] px-3 py-1">Vue hebdomadaire</span>}>
                  <TrendSvg />
                </Panel>

                <Panel title="Performance transport" right={<span className="text-[#16b36c]">92,3%</span>}>
                  <div className="flex flex-wrap items-center gap-6">
                    <div className="relative flex h-24 w-24 items-center justify-center">
                      <div className="absolute h-24 w-24 rounded-full border-[8px] border-[#e9eef6]" />
                      <div className="absolute h-24 w-24 rounded-full border-[8px] border-[#27bf99] border-r-[#f4c53b] border-b-transparent rotate-[35deg]" />
                      <span className="text-xl font-bold text-[#253245]">72%</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-[#ff8b4a] px-2 py-1 text-[10px] font-bold uppercase text-white">Ponctualite</span>
                        <span className="font-semibold text-[#415063]">Trajets a l'heure</span>
                      </div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#edf2f7]">
                        <div className="h-full w-[58%] bg-[#4f9cf6]" />
                      </div>
                    </div>
                    <div className="grid min-w-[140px] gap-2 text-sm text-[#607084]">
                      <div><span className="mr-2 font-bold text-[#1dbb7b]">35</span> departs a l'heure</div>
                      <div><span className="mr-2 font-bold text-[#1dbb7b]">38</span> lignes sans incident</div>
                      <div><span className="mr-2 font-bold text-[#f05b57]">50</span> alertes traitees</div>
                    </div>
                    <div className="grid min-w-[120px] gap-2 text-sm text-[#607084]">
                      <div><span className="mr-2 font-bold text-[#253245]">2</span> depots</div>
                      <div><span className="mr-2 font-bold text-[#253245]">31</span> arrets actifs</div>
                      <div><span className="mr-2 font-bold text-[#253245]">45</span> minutes gagnees</div>
                    </div>
                  </div>
                </Panel>
              </div>

              <div className="space-y-4">
                <Panel title="Suivi des bus" right={<span className="rounded-full bg-[#eef3fb] px-3 py-1">Vue live</span>}>
                  <div className="space-y-3 overflow-x-auto">
                    <div className="grid min-w-[320px] grid-cols-[60px_70px_1fr_80px] px-3 text-xs uppercase tracking-[0.08em] text-[#93a0b1]">
                      <span>Bus</span>
                      <span>Ligne</span>
                      <span>Chauffeur</span>
                      <span>Vitesse</span>
                    </div>
                    {busRows.map((row) => (
                      <div key={`${row.line}-${row.driver}`} className="grid min-w-[320px] grid-cols-[60px_70px_1fr_80px] items-center rounded-2xl bg-[#f8fafc] px-3 py-3">
                        <img src={row.image} alt="" className="h-11 w-11 object-contain" />
                        <div className="font-bold text-[#253245]">{row.line}</div>
                        <div className="text-sm text-[#253245]">{row.driver}</div>
                        <div className="text-left text-sm font-bold text-[#253245]">{row.speed}</div>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>

              <div className="space-y-4">
                <Panel title="Statistiques securite">
                  <div className="grid gap-3">
                    {securityStats.map(([label, bg, tone]) => (
                      <div key={label} className={`flex flex-col gap-3 rounded-2xl ${bg} px-4 py-4 sm:flex-row sm:items-center sm:justify-between`}>
                        <div className="flex items-center gap-3">
                          <div className={`rounded-xl bg-white/80 p-3 ${tone}`}>
                            <FiShield />
                          </div>
                          <span className="text-sm font-medium text-[#516073]">{label}</span>
                        </div>
                        <div className="flex items-end gap-1">
                          <span className="h-2 w-3 rounded-sm bg-[#62d0c7]" />
                          <span className="h-3 w-3 rounded-sm bg-[#4cbde3]" />
                          <span className="h-5 w-3 rounded-sm bg-[#2cc49c]" />
                        </div>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>
            </div>
            )}

            {visibleSections.fleet && (
            <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-[300px_minmax(0,1fr)_300px_300px]">
              <section className="overflow-hidden rounded-[22px] bg-[radial-gradient(circle_at_top,#1a425c_0%,#0f2335_45%,#091521_100%)] text-white shadow-[0_20px_40px_rgba(10,18,33,0.24)]">
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                  <h3 className="text-[1.1rem] font-bold">Suivi des bus</h3>
                  <div className="text-sm text-white/60">Vue lignes</div>
                </div>
                <div className="space-y-4 p-5">
                  {recentBusCards.map((card) => (
                    <div key={card.title} className="grid grid-cols-[44px_1fr] gap-3 sm:grid-cols-[44px_1fr_48px_1fr] sm:items-center">
                      <img src={card.image} alt="" className="h-10 w-10 object-contain" />
                      <div className="font-semibold">{card.title}</div>
                      <span className="rounded-full bg-[#ff8f64] px-2 py-0.5 text-center text-xs font-bold">{card.badge}</span>
                      <div className="text-sm text-white/75">{card.line} {card.status}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="overflow-hidden rounded-[22px] bg-[radial-gradient(circle_at_top,#1b435d_0%,#0f2335_45%,#091521_100%)] text-white shadow-[0_20px_40px_rgba(10,18,33,0.24)]">
                <div className="hidden grid-cols-[1.1fr_1fr_120px_120px] border-b border-white/10 px-6 py-4 text-sm text-white/60 sm:grid">
                  <span>Presence eleves</span>
                  <span>Detail</span>
                  <span>Vitesse</span>
                  <span>Statut</span>
                </div>
                <div className="space-y-4 p-5">
                  {studentPresence.map((item) => (
                    <div key={`${item.bus}-${item.status}`} className="grid gap-3 sm:grid-cols-[1.1fr_1fr_120px_120px] sm:items-center sm:gap-4">
                      <div className="flex items-center gap-3">
                        <img src={item.image} alt="" className="h-9 w-9 object-contain" />
                        <span className="font-semibold">{item.bus}</span>
                      </div>
                      <div className="text-sm text-white/70">{item.detail}</div>
                      <div className="text-sm">{item.speed}</div>
                      <div>
                        <span className="rounded-full bg-[#1dbb7b] px-3 py-1 text-xs font-bold">{item.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <Panel title="Activite des arrets" right={<span className="rounded-full bg-[#eef3fb] px-3 py-1">Vue du jour</span>}>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {stopStats.map((item) => (
                    <div key={item.label} className="rounded-2xl bg-[#f9fbfd] p-4">
                      <div className="text-[2.2rem] font-bold" style={{ color: item.color }}>{item.value}</div>
                      <div className="text-sm text-[#7f8a9a]">{item.label}</div>
                      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#e3e9f1]">
                        <div className="h-full w-[56%]" style={{ backgroundColor: item.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>

              <Panel title="Suivi maintenance bus" right={<FiMoreHorizontal />}>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {maintenance.map((item) => (
                    <div key={item.label} className={`rounded-2xl px-4 py-5 ${item.bg}`}>
                      <div className={`text-xl font-black ${item.text}`}>{item.icon}</div>
                      <div className="mt-3 text-[2rem] font-bold text-[#253245]">{item.value}</div>
                      <div className="text-sm text-[#7f8a9a]">{item.label}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
            )}

            
          </div>

          {/* KPIs - Tous sur la même ligne (6 + Vitesse max + SOS réduit) */}
          {visibleSections.kpis && (
          <div className="mb-2 mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-1.5">
            {mainKpiCards.map((card) => (
              <div
                key={card.label}
                className={`rounded-2xl border bg-gradient-to-br ${card.style} p-2 shadow-sm`}
              >
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-[10px] font-medium">{card.label}</p>
                </div>
                <p className="text-[14px] leading-none font-bold">{card.value}</p>
                <p className="text-[9px] opacity-80 mt-0.5">{card.sub}</p>
              </div>
            ))}
            {/* Card Vitesse maximale par zone (3+2) - Design amélioré */}
            <div className="rounded-2xl border bg-gradient-to-br from-slate-800 to-slate-900 p-2 shadow-sm text-white col-span-1">
              <p className="text-[7px] font-semibold opacity-80 mb-1 uppercase tracking-wide">Vitesse maximal violation (par zone)</p>
              <div className="grid grid-cols-2 gap-x-1.5 gap-y-0.5">
                <div className="space-y-0.5">
                  {maxSpeedByZone.slice(0, 3).map((item) => (
                    <div key={item.zone} className="flex items-center justify-between">
                      <span className="text-[8px] font-medium opacity-70">Zone {item.zone}</span>
                      <span className="text-[9px] font-bold text-emerald-400">{item.speed}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-0.5">
                  {maxSpeedByZone.slice(3).map((item) => (
                    <div key={item.zone} className="flex items-center justify-between">
                      <span className="text-[8px] font-medium opacity-70">Zone {item.zone}</span>
                      <span className="text-[9px] font-bold text-emerald-400">{item.speed}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* SOS - Réduit et aligné */}
            <div className="rounded-2xl border bg-gradient-to-br from-red-700 to-red-800 p-2 shadow-sm text-white flex flex-col items-center justify-center">
              <p className="text-[9px] font-medium opacity-80">SOS</p>
              <p className="text-[15px] font-bold leading-none">2</p>
            </div>
          </div>
          )}

          <div className="space-y-4">
            <div className="space-y-3">
              {visibleSections.liveMaps && (
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-12 lg:col-span-6">
                    <div className="h-[520px]">
                     {/*  <MapOverviewTrafficLite
                        ref={mapOverviewRef}
                        weekdayFilter={weekdayFilter}
                        onWeekdayFilterChange={setWeekdayFilter}
                        weekdayOptions={WEEKDAY_OPTIONS}
                      /> */}
                      <TrackingPageLite2
                        isExpanded={isTrackingExpanded}
                        onExpand={() => setIsTrackingExpanded(true)}
                        onClose={() => setIsTrackingExpanded(false)}
                      />
                    </div>
                  </div>
                  <div className="col-span-12 lg:col-span-6">
                    <div className="h-[520px]">
                      <TrackingPageLite
                        isExpanded={isTrackingExpanded}
                        onExpand={() => setIsTrackingExpanded(true)}
                        onClose={() => setIsTrackingExpanded(false)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {visibleSections.incidentDeviation && (
              <div className="grid grid-cols-12 gap-3 pt-6">
                <DarkCard title="Évolution des incidents" className="col-span-12 lg:col-span-6 pt-5">
                  <WeekdayFilterSelect value={weekdayFilter} onChange={(e) => setWeekdayFilter(e.target.value)} />
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={filteredIncidentTrendData}>
                        <CartesianGrid stroke="#475569" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="hour" stroke="#64748b" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#64748b" tick={{ fontSize: 10 }} domain={[0, 12]} ticks={[0, 3, 6, 9, 12]} />
                        <Tooltip />
                        {activeIncidentSeries.map((series) => (
                          <Line
                            key={`incident-line-${series.key}`}
                            type="monotone"
                            dataKey={series.key}
                            name={series.label}
                            stroke={series.color}
                            strokeWidth={3}
                            dot={{ r: 4, stroke: series.color, strokeWidth: 2, fill: "#fff" }}
                            activeDot={{ r: 5 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedIncidentSeriesKey(null);
                        setIncidentPage(1);
                      }}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 transition-colors ${
                        !selectedIncidentSeriesKey
                          ? "border-slate-400 bg-slate-100 text-foreground"
                          : "border-border bg-card text-muted-foreground"
                      }`}
                    >
                      <span className="h-2.5 w-2.5 rounded-full bg-slate-500" />
                      Tous
                    </button>
                    {INCIDENT_SERIES.map((series) => (
                      <button
                        key={`incident-legend-${series.key}`}
                        type="button"
                        onClick={() => {
                          setSelectedIncidentSeriesKey((prev) => (prev === series.key ? null : series.key));
                          setIncidentPage(1);
                        }}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 transition-colors ${
                          !selectedIncidentSeriesKey || selectedIncidentSeriesKey === series.key
                            ? "border-slate-400 bg-slate-100 text-foreground"
                            : "border-border bg-card text-muted-foreground"
                        }`}
                      >
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: series.color }} />
                        {series.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3 overflow-x-auto rounded-xl border border-border">
                    <table className="min-w-full text-xs">
                      <thead className="bg-secondary/60 text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 text-left">Action</th>
                          <th className="px-3 py-2 text-left">Véhicule</th>
                          <th className="px-3 py-2 text-left">Date incident</th>
                          <th className="px-3 py-2 text-left">Localisation</th>
                          <th className="px-3 py-2 text-left">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedIncidentRows.length > 0 ? (
                          paginatedIncidentRows.map((row, idx) => (
                            <tr key={`${row.vehicle}-${row.incidentDate}-${idx}`} className="border-t border-border">
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1">
                                  {/* Replay Trip - Bleu */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedIncident(row);
                                      setIsReplayModalOpen(true);
                                    }}
                                    className="inline-flex items-center gap-1 rounded-md border border-blue-600 bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 transition-colors"
                                    title="Replay du trajet"
                                  >
                                    <Eye className="w-3 h-3" />
                                    Replay Trip
                                  </button>
                                  {/* Replay Violation - Rouge */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedIncident(row);
                                      setIsReplayModalOpen(true);
                                    }}
                                    className="inline-flex items-center gap-1 rounded-md border border-red-600 bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 transition-colors"
                                    title="Replay de la violation"
                                  >
                                    <Eye className="w-3 h-3" />
                                    Replay Violation
                                  </button>
                                </div>
                              </td>
                              <td className="px-3 py-2">{row.vehicle}</td>
                              <td className="px-3 py-2">{row.incidentDate}</td>
                              <td className="px-3 py-2">{row.location}</td>
                              <td className="px-3 py-2">
                                <span
                                  className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                    row.type === "Panic bouton"
                                      ? "bg-red-100 text-red-700"
                                      : row.type === "Accélération brusque"
                                      ? "bg-orange-100 text-orange-700"
                                      : row.type === "Freinage brusque"
                                      ? "bg-yellow-100 text-yellow-700"
                                      : row.type === "Excès de vitesse"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-blue-100 text-blue-700"
                                  }`}
                                >
                                  {row.type}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">
                              Aucune donnée pour ce type
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {totalIncidentPages > 1 && (
                    <div className="mt-2 flex items-center justify-end gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => setIncidentPage((prev) => Math.max(1, prev - 1))}
                        disabled={incidentPage === 1}
                        className="rounded-md border border-border bg-card px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>
                      <span className="text-muted-foreground">
                        Page {incidentPage}/{totalIncidentPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setIncidentPage((prev) => Math.min(totalIncidentPages, prev + 1))}
                        disabled={incidentPage === totalIncidentPages}
                        className="rounded-md border border-border bg-card px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </div>
                  )}
                </DarkCard>

                <DarkCard title="Déviation" className="col-span-12 lg:col-span-6">
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {deviationSummaryCards.map((item) => (
                        <div key={item.key} className="rounded-xl border border-border bg-secondary/30 p-3">
                          <p className="text-xs font-medium text-muted-foreground">{item.title}</p>
                          <p className="mt-1 text-2xl font-bold text-foreground">
                            {item.value}
                            {item.unit ? <span className="ml-1 text-sm font-medium text-muted-foreground">{item.unit}</span> : null}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                      <div className="xl:col-span-2 rounded-xl border border-border bg-secondary/20 p-2">
                        <WeekdayFilterSelect value={weekdayFilter} onChange={(e) => setWeekdayFilter(e.target.value)} />
                        <div className="text-xs font-semibold text-foreground mb-2">
                          Évolution Horaire du Taux de Déviation des Trajets Prévus
                        </div>
                        <RouteDeviationTrendChart dayFactor={(DAY_FACTORS[weekdayFilter] || DAY_FACTORS.all).deviations} />
                      </div>
                      <div className="xl:col-span-1">
                        <DeviationTopRankingCard />
                      </div>
                    </div>
                  </div>
                </DarkCard>
              </div>
              )}

              {visibleSections.speedZones && (
              <DarkCard title="Zones de limitations de vitesse" className="col-span-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4 gap-3">
                  {speedLimitationZones.map((zone) => (
                    <div key={zone.limitation} className={`rounded-xl border p-3 ${zone.cardClass}`}>
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className={`text-sm font-bold ${zone.titleClass}`}>{zone.title}</h4>
                        <button className="rounded-md border border-border bg-white/80 p-1">
                          <Maximize2 className="w-3.5 h-3.5 text-gray-600" />
                        </button>
                      </div>

                      <div className="mb-2">
                        <select
                          value={weekdayFilter}
                          onChange={(e) => setWeekdayFilter(e.target.value)}
                          className="h-10 w-full rounded-md border border-border bg-white px-3 pr-9 text-sm leading-5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          {WEEKDAY_OPTIONS.map((option) => (
                            <option key={`zone-${zone.limitation}-${option.value}`} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1.5 mb-3 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Véhicules</span>
                          <span className="font-bold text-gray-900">{zone.vehicles}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Vitesse max</span>
                          <span className="font-bold text-red-600">{zone.maxSpeed} km/h</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Vitesse moy</span>
                          <span className="font-bold text-gray-900">{zone.avgSpeed} km/h</span>
                        </div>
                      </div>

                      <div className="mb-3 h-[180px] w-full rounded-md border border-white/60 bg-white/70 p-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={zone.trendData.map((point) => ({
                              ...point,
                              value: Math.max(
                                0,
                                Math.round(point.value * ((DAY_FACTORS[weekdayFilter] || DAY_FACTORS.all).incidents || 1))
                              ),
                            }))}
                          >
                            <defs>
                              <linearGradient id={`zoneGradient-${zone.limitation}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={zone.trendColor} stopOpacity={0.2} />
                                <stop offset="100%" stopColor={zone.trendColor} stopOpacity={0.02} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#475569" strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} width={28} />
                            <Tooltip />
                            <Area
                              type="monotone"
                              dataKey="value"
                              stroke={zone.trendColor}
                              strokeWidth={2}
                              fill={`url(#zoneGradient-${zone.limitation})`}
                              dot={{ r: 4, strokeWidth: 2, fill: "#fff", stroke: zone.trendColor }}
                              activeDot={{ r: 5 }}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="rounded-lg border border-border bg-white p-1.5">
                        <LimitationZoneRankingCard
                          limitation={zone.limitation}
                          colorTheme={zone.colorTheme}
                          label={`Violations ${zone.limitation}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </DarkCard>
              )}

              {visibleSections.trafficFlow && (
                <DarkCard
                  title="Évolution horaire des Flux de Trafic et de la Vitesse Moyenne"
                  className="col-span-12 lg:col-span-6"
                >
                  <WeekdayFilterSelect value={weekdayFilter} onChange={(e) => setWeekdayFilter(e.target.value)} />
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={filteredMultiTrendData}>
                        <CartesianGrid stroke="#e2e8f0" strokeDasharray="2 3" />
                        <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line
                          type="monotone"
                          dataKey="volumeTrafic"
                          name="Volume de trafic"
                          stroke="#ef4444"
                          strokeWidth={3}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="vitesseMoyenne"
                          name="Vitesse moyenne"
                          stroke="#06b6d4"
                          strokeWidth={3}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </DarkCard>
              )}
            </div>
          </div>

          {isAlertsModalOpen && selectedItemType && (
            <div className="fixed inset-0 z-[120] flex justify-end">
              <div 
                className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
                onClick={() => setIsAlertsModalOpen(false)}
              />
              <div className="relative h-full w-full max-w-md border-l border-border bg-card shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
                <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-secondary/20">
                  <div className="flex flex-col">
                    <h3 className="text-lg font-bold text-foreground">
                      {selectedItemType === "commentaires" ? "Commentaires" : "Alertes actives"}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {selectedItemType === "commentaires" ? `${commentItems.length} messages` : `${alertItems.length} alertes en cours`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsAlertsModalOpen(false);
                      setSelectedItemType(null);
                      setAlertsModalPage(1);
                    }}
                    className="rounded-full border border-border p-2 text-muted-foreground hover:bg-secondary/60 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  <div className="mb-4 flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedItemType("alertes");
                        setAlertsModalPage(1);
                      }}
                      className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                        selectedItemType === "alertes"
                          ? "bg-red-600 text-white shadow-sm"
                          : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                      }`}
                    >
                      Alertes ({alertItems.length})
                    </button>
                    <button
                      onClick={() => {
                        setSelectedItemType("commentaires");
                        setAlertsModalPage(1);
                      }}
                      className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                        selectedItemType === "commentaires"
                          ? "bg-blue-600 text-white shadow-sm"
                          : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                      }`}
                    >
                      Commentaires ({commentItems.length})
                    </button>
                  </div>

                  {displayedModalItems.map((item) => (
                    <div key={`modal-item-${item.id}`} className="mb-4 rounded-xl border border-border bg-secondary/20 p-4 shadow-sm">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="inline-block h-3 w-3 rounded-full shadow-sm" style={{ backgroundColor: item.color }} />
                          <span className="font-bold text-foreground">{item.label}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                          <span className="inline-flex items-center gap-1 rounded-full bg-card px-2 py-0.5 border border-border">
                            <Clock3 className="w-3 h-3" />
                            {item.arrivedAt}
                          </span>
                          {selectedItemType === "commentaires" ? (
                            <span className="inline-flex rounded-full bg-card px-2 py-0.5 border border-border">{item.author}</span>
                          ) : (
                            <>
                              <span className="inline-flex rounded-full bg-card px-2 py-0.5 border border-border">{item.status}</span>
                              <span className="inline-flex rounded-full bg-card px-2 py-0.5 border border-border">{item.vehicle}</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {selectedItemType === "commentaires" && (
                        <div className="mt-3 space-y-2">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={alertResponses[item.id] || ""}
                              onChange={(e) => setAlertResponses((prev) => ({ ...prev, [item.id]: e.target.value }))}
                              placeholder="Répondre..."
                              className="h-9 flex-1 rounded-lg border border-border bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setSentResponses((prev) => ({
                                  ...prev,
                                  [item.id]: Boolean((alertResponses[item.id] || "").trim()),
                                }))
                              }
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                          {sentResponses[item.id] && (
                            <p className="flex items-center gap-1 text-[10px] font-medium text-emerald-600">
                              <CheckCircle2 className="w-3 h-3" /> Réponse envoyée avec succès
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {selectedItemType === "alertes" && alertItems.length > alertModalPageSize && (
                    <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
                      <button
                        type="button"
                        onClick={() => setAlertsModalPage((prev) => Math.max(1, prev - 1))}
                        disabled={alertsModalPage === 1}
                        className="flex items-center gap-1 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary disabled:opacity-40"
                      >
                        Précédent
                      </button>
                      <span className="text-xs font-medium text-muted-foreground">
                        Page {alertsModalPage} sur {totalAlertModalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setAlertsModalPage((prev) => Math.min(totalAlertModalPages, prev + 1))}
                        disabled={alertsModalPage === totalAlertModalPages}
                        className="flex items-center gap-1 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary disabled:opacity-40"
                      >
                        Suivant
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {isReplayModalOpen && selectedIncident && (
            <TripReplayModal
              isOpen={isReplayModalOpen}
              onClose={() => {
                setIsReplayModalOpen(false);
                setSelectedIncident(null);
              }}
              tripData={tripData}
              loading={tripLoading}
            />
          )}
        </div>
      </div>

      {/* Bouton flottant pour les alertes et commentaires */}
      <div className="fixed bottom-8 right-8 z-[100]">
        <button
          type="button"
          onClick={() => {
            setSelectedItemType("alertes");
            setAlertsModalPage(1);
            setIsAlertsModalOpen(true);
          }}
          className="group relative flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white shadow-2xl transition-all hover:scale-110 hover:bg-red-700 active:scale-95"
        >
          <Bell className="h-8 w-8" />
          {alertItems.length > 0 && (
            <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-white text-[10px] font-bold text-red-600 border-2 border-red-600 shadow-md">
              {alertItems.length}
            </span>
          )}
          <div className="absolute right-full mr-4 whitespace-nowrap rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none">
            Alertes & Commentaires
          </div>
        </button>
      </div>
    </div>
  );
});

export default TrafficPageLite;

